import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ErrorBox, explainProblem, type Explained } from '../ErrorBox';
import { useOptionalHelp } from '../help';
import { Icon } from '../icons';
import { Meter, cx } from '../layout';

export interface FlowStep {
  id: string;
  title: string;
  lead?: ReactNode;
  render: () => ReactNode;
  /** Erro explicado quando o passo ainda não pode avançar (o que houve / por quê / como resolver). */
  validate?: () => Explained | null | Promise<Explained | null>;
}

export interface StepFlowProps<R> {
  /** Identificador (ajuda lateral e ids de acessibilidade). */
  id: string;
  title: string;
  steps: FlowStep[];
  onFinish: () => Promise<R>;
  renderDone?: (result: R) => ReactNode;
  finishLabel?: string;
  onCancel?: () => void;
  initialStep?: number;
}

/**
 * Assistente com passos montados pela tela (quando o conteúdo não cabe num esquema do núcleo:
 * construtor de condições, prévia, diagnóstico). Mesmo visual e comportamento do Wizard por esquema:
 * passos numerados, voltar sem perder dados, erro explicado no passo e foco no título a cada troca.
 */
export function StepFlow<R>(p: StepFlowProps<R>) {
  const { t, lang, has } = useI18n<CommonKey>();
  const help = useOptionalHelp();
  const [step, setStep] = useState(Math.min(p.initialStep ?? 0, p.steps.length - 1));
  const [error, setError] = useState<Explained | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ result: R } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  const cur = p.steps[Math.min(step, p.steps.length - 1)]!;
  const last = step >= p.steps.length - 1;

  useEffect(() => {
    if (first.current) first.current = false;
    else heading.current?.focus({ preventScroll: false });
    help?.setWizard(done ? null : { id: p.id, stepId: cur.id, n: step + 1, total: p.steps.length, title: cur.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avisa só quando o passo muda
  }, [step, done]);
  useEffect(() => () => help?.setWizard(null), [help?.setWizard]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (i: number) => {
    setError(null);
    setStep(i);
  };

  async function next() {
    if (busy) return;
    setBusy(true);
    try {
      const problem = cur.validate ? await cur.validate() : null;
      if (problem) {
        setError(problem);
        heading.current?.focus();
        return;
      }
      if (!last) {
        goTo(step + 1);
        return;
      }
      const result = await p.onFinish();
      setDone({ result });
    } catch (e) {
      setError(explainProblem(e, { t, lang, has }));
      heading.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const headingId = `sf-${p.id}-h`;
  if (done) {
    return (
      <div className="wz">
        <section className="wz-body" aria-labelledby={headingId}>
          <h2 id={headingId} ref={heading} tabIndex={-1} className="sr-only">
            {p.title}
          </h2>
          {p.renderDone?.(done.result)}
        </section>
      </div>
    );
  }
  const total = p.steps.length;
  const pct = Math.round((step / total) * 100);
  return (
    <div className="wz">
      <div className="wz-head">
        <ol className="wz-steps" aria-label={t('wz.steps')}>
          {p.steps.map((s, i) => (
            <li key={s.id} className={cx('wz-step', i < step && 'done', i === step && 'cur')}>
              <button
                type="button"
                onClick={i < step ? () => goTo(i) : undefined}
                tabIndex={i < step ? 0 : -1}
                aria-disabled={i < step ? undefined : true}
                aria-current={i === step ? 'step' : undefined}
                aria-label={t('wz.stepOf', { n: i + 1, total, title: s.title })}
              >
                <span className="bar" />
                <span className="lbl">
                  <span className="n">{i + 1}</span>
                  <span className="t">{s.title}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
        <div className="wz-mobile">
          <div className="row between">
            <b>{t('wz.stepOf', { n: step + 1, total, title: cur.title })}</b>
            <span className="muted small">{pct}%</span>
          </div>
          <Meter value={step + 1} max={total} label={t('wz.stepOf', { n: step + 1, total, title: cur.title })} />
        </div>
      </div>
      <section className="wz-body" aria-labelledby={headingId}>
        <h2 id={headingId} ref={heading} tabIndex={-1}>
          {step + 1}. {cur.title}
        </h2>
        {cur.lead && <p className="lead">{cur.lead}</p>}
        {error && <ErrorBox e={error} />}
        <div className="stack">{cur.render()}</div>
      </section>
      <div className="wz-foot">
        {p.onCancel && (
          <button type="button" className="btn btn-ghost" onClick={p.onCancel}>
            {t('ui.cancel')}
          </button>
        )}
        <span className="wz-saved" aria-live="polite">
          {busy ? t('wz.checking') : ''}
        </span>
        <span className="spacer" />
        {step > 0 && (
          <button type="button" className="btn" onClick={() => goTo(step - 1)}>
            <Icon name="arrowL" size="sm" />
            {t('ui.back')}
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={next} disabled={busy} aria-busy={busy || undefined}>
          {last ? p.finishLabel ?? t('wz.finish') : t('ui.next')}
          {!last && <Icon name="arrowR" size="sm" />}
        </button>
      </div>
    </div>
  );
}
